import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { styles } from '../../styles/styles';
import useWindowResize from '../../hooks/useWindowResize';
import { useThoughtCompose } from '../../hooks/useThoughtCompose';
import { getThoughtById } from '../../services/thoughtService';

import MobileLayout from './layouts/MobileLayout';
import DesktopLayout from './layouts/DesktopLayout';
import ThoughtEditor from './ThoughtEditor';
import ActionButtons from './ActionButtons';
import NavigationButton from './NavigationButton';

// Inner form. Extracted so it mounts only once `existingThought` is resolved —
// useThoughtCompose seeds the body text from existingThought at mount time, so
// a late async populate would otherwise leave the editor empty. The parent
// keys this component on the resolved id to force a fresh seed.
const ComposeForm = ({ onSave, isEditing, existingThought, thoughtId }) => {
    const navigate = useNavigate();
    const isMobile = useWindowResize();
    const { thought, setThought, isSaving, handleSave } = useThoughtCompose({
        onSave,
        isEditing,
        existingThought,
        thoughtId,
    });

    const handleClose = () => isEditing ? navigate(`/thought/${thoughtId}`) : navigate('/');
    const Layout = isMobile ? MobileLayout : DesktopLayout;

    return (
        <Layout styles={styles} onClose={handleClose}>
            <NavigationButton
                onClick={handleClose}
                isMobile={isMobile}
                styles={styles}
            />

            <ThoughtEditor
                value={thought}
                onChange={setThought}
                styles={styles}
                isMobile={isMobile}
            />

            <ActionButtons
                isEditing={isEditing}
                isSaving={isSaving}
                onSave={handleSave}
                thought={thought}
                styles={styles}
                isMobile={isMobile}
            />
        </Layout>
    );
};

const ComposePage = ({ onSave, isEditing = false, thoughts = [] }) => {
    const { id } = useParams();
    const navigate = useNavigate();
    const isMobile = useWindowResize();

    const fromProps = isEditing && id ? (thoughts.find(t => t.id === id) || null) : null;
    const [existingThought, setExistingThought] = useState(fromProps);
    // We only need to resolve asynchronously when editing a thought that isn't
    // already in the (drafts-only) `thoughts` prop — i.e. a triaged thought.
    const [resolving, setResolving] = useState(isEditing && !!id && !fromProps);

    useEffect(() => {
        if (!isEditing || !id) { setResolving(false); return; }

        const local = thoughts.find(t => t.id === id);
        if (local) { setExistingThought(local); setResolving(false); return; }

        let cancelled = false;
        setResolving(true);
        getThoughtById(id)
            .then(fetched => {
                if (cancelled) return;
                setExistingThought(fetched || null);
                setResolving(false);
            })
            .catch(() => {
                if (cancelled) return;
                setExistingThought(null);
                setResolving(false);
            });
        return () => { cancelled = true; };
    }, [isEditing, id, thoughts]);

    if (resolving) {
        const Layout = isMobile ? MobileLayout : DesktopLayout;
        const handleClose = () => navigate(`/thought/${id}`);
        return (
            <Layout styles={styles} onClose={handleClose}>
                <NavigationButton onClick={handleClose} isMobile={isMobile} styles={styles} />
            </Layout>
        );
    }

    // key forces ComposeForm (and the useThoughtCompose seed) to remount once
    // existingThought resolves from null -> fetched.
    return (
        <ComposeForm
            key={existingThought?.id || (isEditing ? `edit-${id}` : 'new')}
            onSave={onSave}
            isEditing={isEditing}
            existingThought={existingThought}
            thoughtId={id}
        />
    );
};

export default ComposePage;
